<script setup lang="ts">
import { IConfirmDialogProps, useConfirmDialog } from '@/composables/useConfirmDialog';
import DialogHeader from '@/core/dialogs/base-dialog/DialogHeader.vue';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import { nextTick, onBeforeUnmount, ref, useTemplateRef } from 'vue';

const { confirm } = useConfirmDialog();

const dialogRef = useTemplateRef<InstanceType<typeof NativeDialog>>('dialogRef');
const contentRef = useTemplateRef('contentRef');

const isShown = ref(false);
const hasCompletedEnterAnimation = ref(true);
let enterAnimationFrame: number | null = null;

const emit = defineEmits<{
  hide: [];
  'click:back': [];
}>();

const props = withDefaults(
  defineProps<{
    title?: string;
    noPadding?: boolean;
    heroImage?: string;
    fillHeight?: boolean;
    fillWidth?: boolean;
    contentWidthStyle?: string;
    dialogHeightStyle?: string;
    showCloseButton?: boolean;
    showBackButton?: boolean;
    confirmConfig?: IConfirmDialogProps;
    headerTheme?: 'default' | 'checkmark' | 'note';
    showHeader?: boolean;
    headerBordered?: boolean;
    headerScrollable?: boolean;
    disableInternalHideLogic?: boolean;
    enterAnimation?: 'none' | 'slide-up';
    mobilePresentation?: 'fullscreen' | 'bottom-sheet';
  }>(),
  {
    title: undefined,
    heroImage: undefined,
    showCloseButton: true,
    confirmConfig: undefined,
    showBackButton: false,
    headerTheme: 'default',
    showHeader: true,
    headerBordered: false,
    headerScrollable: false,
    disableInternalHideLogic: false,
    contentWidthStyle: undefined,
    dialogHeightStyle: undefined,
    enterAnimation: 'none',
    mobilePresentation: 'fullscreen',
  },
);

const cancelEnterAnimation = (): void => {
  if (enterAnimationFrame != null) {
    cancelAnimationFrame(enterAnimationFrame);
    enterAnimationFrame = null;
  }
};

const startEnterAnimation = (): void => {
  if (props.enterAnimation === 'none') return;

  cancelEnterAnimation();
  enterAnimationFrame = requestAnimationFrame(() => {
    enterAnimationFrame = requestAnimationFrame(() => {
      hasCompletedEnterAnimation.value = true;
      enterAnimationFrame = null;
    });
  });
};

const hide = (): void => {
  cancelEnterAnimation();
  hasCompletedEnterAnimation.value = true;
  emit('hide');
  dialogRef.value?.close();
  isShown.value = false;
};

const shouldShowConfirm = (): boolean => props.confirmConfig?.showConfirmOn?.(undefined) ?? true;

const confirmHide = async (): Promise<void> => {
  if (!props.confirmConfig) return;
  if (!shouldShowConfirm()) {
    hide();
    return;
  }

  const confirmed = await confirm(props.confirmConfig);
  if (!confirmed) return;
  hide();
};

const tryToHide = (): void => {
  if (props.confirmConfig) {
    void confirmHide();
  } else {
    hide();
  }
};

const show = async (): Promise<void> => {
  cancelEnterAnimation();
  hasCompletedEnterAnimation.value = props.enterAnimation === 'none';
  isShown.value = true;
  await nextTick();
  dialogRef.value?.showModal();
  startEnterAnimation();
};

const handleNativeClose = (): void => {
  isShown.value = false;
};

const scrollToTop = (): void => {
  contentRef.value?.scrollTo({ behavior: 'smooth', top: 0 });
};

const handleBackdropClick = (event: MouseEvent): void => {
  if (!props.disableInternalHideLogic) {
    tryToHide();
  }
  event.stopPropagation();
  event.preventDefault();
};

const handleCancel = (event: Event): void => {
  event.preventDefault();

  if (props.disableInternalHideLogic) return;

  tryToHide();
};

defineExpose({
  hide,
  show,
  scrollToTop,
  tryToHide,
});

onBeforeUnmount(cancelEnterAnimation);
</script>

<template>
  <NativeDialog
    v-if="isShown"
    ref="dialogRef"
    :class="[
      {
        'dialog-slide-up-motion': enterAnimation === 'slide-up',
        'dialog-slide-up-enter-from': enterAnimation === 'slide-up' && !hasCompletedEnterAnimation,
        'dialog-slide-up-enter-to': enterAnimation === 'slide-up' && hasCompletedEnterAnimation,
      },
      'rounded-dialog',
      mobilePresentation === 'bottom-sheet'
        ? [
            'overflow-hidden max-h-80vh',
            'max-md:fixed max-md:inset-0 max-md:m-0 max-md:h-dvh max-md:max-h-none max-md:w-screen max-md:max-w-none max-md:overflow-visible max-md:rounded-none max-md:bg-transparent max-md:outline-none',
          ]
        : 'overflow-hidden max-h-80vh max-md:inset-0 max-md:m-0 max-md:w-screen max-md:max-w-full max-md:h-dvh max-md:max-h-dvh max-md:rounded-none',
      { 'h-[700px]': fillHeight },
      { 'w-[960px] max-w-[calc(100vw-32px)]': fillWidth },
      !fillWidth ? (contentWidthStyle ?? 'w-[600px]') : '',
      dialogHeightStyle ?? '',
      'border-0 p-0', // Important for backdrop click detection to work across browsers
    ]"
    :close-on-backdrop-click="false"
    :close-on-cancel="false"
    @backdrop-click="handleBackdropClick"
    @cancel="handleCancel"
    @close="handleNativeClose"
  >
    <div
      ref="dialogRefContent"
      :class="[
        'flex flex-col [container-type:inline-size]',
        mobilePresentation === 'bottom-sheet'
          ? [
              'max-h-80vh',
              'max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:h-auto max-md:max-h-mobile-drawer max-md:w-screen max-md:max-w-full max-md:overflow-hidden max-md:rounded-t-dialog max-md:bg-white max-md:shadow-popover',
            ]
          : 'max-h-80vh max-md:w-screen max-md:max-w-full max-md:h-dvh max-md:max-h-dvh max-md:shadow-none',
        { 'w-[960px] max-w-[calc(100vw-32px)]': fillWidth },
        !fillWidth ? (contentWidthStyle ?? 'w-[600px]') : '',
        'shadow-popover',
        { 'h-[700px]': fillHeight },
        dialogHeightStyle ?? '',
      ]"
    >
      <slot name="above-header"></slot>
      <img v-if="heroImage" :src="heroImage" class="w-full h-[280px] object-cover" />
      <template v-else>
        <DialogHeader
          v-if="showHeader"
          :show-close-button="showCloseButton"
          :show-back-button="showBackButton"
          :title="title"
          :theme="headerTheme"
          :bordered="headerBordered"
          :scrollable="headerScrollable"
          :is-wide-layout="fillWidth"
          @click:hide="tryToHide"
          @click:back="emit('click:back')"
        >
          <template v-if="$slots.title" #title>
            <slot name="title"></slot>
          </template>
          <template #actions>
            <slot name="actions"></slot>
          </template>
        </DialogHeader>
      </template>

      <slot name="navigation"></slot>

      <div ref="contentRef" :class="['px-content flex-1 max-md:px-lg', { '!p-0': noPadding }, 'overflow-y-auto']">
        <slot />
      </div>

      <footer
        v-if="$slots.footer"
        :class="['flex items-center', 'px-content py-lg max-md:px-lg max-md:pb-mobile-safe-bottom-lg max-md:pt-md']"
      >
        <slot name="footer"></slot>
      </footer>
    </div>
  </NativeDialog>
</template>

<style>
.dialog-slide-up-motion {
  transition:
    transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1),
    opacity 220ms ease-out;
  will-change: transform, opacity;
}

.dialog-slide-up-enter-from {
  transform: translateY(40vh) scale(0.98);
  opacity: 0;
}

.dialog-slide-up-enter-to {
  transform: translateY(0) scale(1);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .dialog-slide-up-motion {
    transition: none;
  }

  .dialog-slide-up-enter-from,
  .dialog-slide-up-enter-to {
    transform: none;
    opacity: 1;
  }
}
</style>
